/* eslint-disable import/order */
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useContext, useState, useEffect, useRef } from 'react';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { HealthcareEntitiesService, FacilityList } from '../../../../api';
import { Pagination } from '../../../../app/components/Pagination/Pagination';
import { useSearchParamsState } from '../../../../app/hooks/search-params';
import { useDebounce } from '../../../../../v2/js/hooks/useDebounce';
import { healthcareEntityFacilityQueryKeys } from '../../../../app/query-keys-factories/facilities';
import 'react-range-slider-input/dist/style.css';
import styles from './FacilitiesList.module.scss';
import { Card } from '../../../../app/components/Card';
import { LinkContainer } from 'react-router-bootstrap';
import { SessionContext } from '../../../../app/contexts/SessionContext';
import { Button, Form } from 'react-bootstrap';
import { BsEye, BsSearch } from 'react-icons/bs';
import { format, parseISO } from 'date-fns';
import { AddNewFacilityDialog } from '../../../../../v2/js/pages/Organization/AddNewFacilityDialog';
import { FacilityTypeEnum } from '../../../../api/models/FacilityTypeEnum';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const PAGE_SIZE = 25;

interface FacilitiesListProps {
  showAddFacilityButton?: boolean;
}

const ViewFacilityButton = ({ facility }: { facility: FacilityList }) => {
  return (
    <LinkContainer to={`/app/organization/facilities/${facility.id}`}>
      <Button className="shadow" size="sm" variant="outline-primary" title="View">
        <BsEye />
      </Button>
    </LinkContainer>
  );
};

const columns: ColumnDef<FacilityList>[] = [
  {
    header: 'Name',
    accessorKey: 'name',
    size: 25,
    minSize: 0,
  },
  {
    header: 'Location',
    accessorFn: (row) => [row.city, row.state].filter(Boolean).join(', '),
    size: 20,
    minSize: 0,
  },
  {
    id: 'facility_type',
    header: 'Facility Type',
    accessorFn: (row) => window.FACILITY_TYPES[row.facility_type] || row.facility_type,
    cell: (props) => <div className="">{String(props.getValue())}</div>,
    size: 20,
    minSize: 0,
  },

  {
    id: 'emr',
    header: 'EMR',
    accessorFn: (row) => {
      const emrValue = row.emr;
      if (emrValue && typeof emrValue === 'string' && window.EMR_CHOICES) {
        return window.EMR_CHOICES[emrValue as keyof typeof window.EMR_CHOICES] || emrValue;
      }
      return emrValue || '';
    },
    cell: (props) => <div className="">{String(props.getValue())}</div>,
    size: 13,
    minSize: 0,
  },
  {
    id: 'updated_at',
    header: () => <div className="text-end">Last Updated</div>,
    accessorFn: (row) => {
      const dateValue = row.updated_at; // replace with actual field name
      return dateValue ? format(parseISO(dateValue), 'MMM d, yyyy') : '';
    },

    cell: (props) => <div className="text-end">{String(props.getValue())}</div>,
    size: 13,
    minSize: 0,
  },
  {
    header: 'Actions',
    id: 'actions',
    cell: (props) => (
      <div className="d-flex justify-content-start gap-2">
        <ViewFacilityButton facility={props.row.original} />
      </div>
    ),
    size: 5,
    minSize: 0,
  },
];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const FacilitiesList = ({ showAddFacilityButton = false }: FacilitiesListProps) => {
  const { user } = useContext(SessionContext);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const prevSearchRef = useRef(debouncedSearch);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const canEditFacility = Boolean(user?.group_names?.includes('manage_facilities'));

  const [currentPageStr, setCurrentPage] = useSearchParamsState('page', '1');
  const currentPage = parseInt(currentPageStr);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      setCurrentPage('1');
      prevSearchRef.current = debouncedSearch;
    }
  }, [debouncedSearch]);

  const handleAddFacility = async (data: { name: string; type: string }) => {
    setIsSubmitting(true);
    try {
      const response = await HealthcareEntitiesService.healthcareEntitiesFacilitiesCreate({
        requestBody: {
          name: data.name,
          facility_type: data.type as FacilityTypeEnum,
        },
      });
      toast.success('Facility created successfully');
      setIsDialogOpen(false);
      navigate(`/app/organization/facilities/${response.id}`);
    } catch (error) {
      console.error('Failed to create facility:', error);
      toast.error('Failed to create facility. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const params: Parameters<typeof HealthcareEntitiesService.healthcareEntitiesFacilitiesList>[0] = {
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    search: debouncedSearch || undefined,
  };

  const queryFacilities = useQuery({
    queryKey: healthcareEntityFacilityQueryKeys.list(params),
    queryFn: async () => {
      return await HealthcareEntitiesService.healthcareEntitiesFacilitiesList(params);
    },
    keepPreviousData: true,
  });

  const table = useReactTable({
    data: queryFacilities?.data?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleChangePage = (page: number) => {
    setCurrentPage(page.toString());
  };

  const renderItems = () => {
    if (queryFacilities.isLoading || queryFacilities.isFetching) {
      return <SkeletonTableRows />;
    }

    if (queryFacilities.isError) {
      return (
        <TableRow>
          <td colSpan={6} className="text-center">
            Error
          </td>
        </TableRow>
      );
    }

    if (queryFacilities.data?.count === 0) {
      return (
        <TableRow>
          <td colSpan={6} className="text-center py-4">
            {debouncedSearch ? (
              <div>
                <p className="text-muted mb-1">
                  No facilities found matching &ldquo;{debouncedSearch}&rdquo;
                </p>
                <small className="text-muted">Try adjusting your search terms</small>
              </div>
            ) : (
              <p className="text-muted">No facilities found</p>
            )}
          </td>
        </TableRow>
      );
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
        ))}
      </TableRow>
    ));
  };

  return (
    <>
      <Row className="justify-content-center" xs={'auto'}>
        <Col>
          <Alert variant="danger" show={queryFacilities.isError}>
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>Please try again later. If the problem persists, please contact us.</p>
          </Alert>
        </Col>
      </Row>
      <Row className="justify-content-center" xs={12}>
        <Col>
          <Card className="mb-3">
            <div className="p-3">
              <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                <div className="position-relative flex-grow-1">
                  <BsSearch className="position-absolute top-50 start-0 translate-middle-y text-muted ms-2" />
                  <Form.Control
                    type="text"
                    placeholder="Search name, city, state"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingLeft: '2rem' }}
                  />
                </div>
                {showAddFacilityButton && canEditFacility && (
                  <Button variant="primary" onClick={() => setIsDialogOpen(true)} className="ms-3">
                    Add Facility
                  </Button>
                )}
              </div>
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle mb-0">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            style={{
                              width: `${header.getSize()}%`,
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="table-group-divider">{renderItems()}</tbody>
                </table>
              </div>
            </div>
          </Card>

          <div className="hstack gap-3 justify-content-center mb-4">
            <span>
              {queryFacilities.data?.count ?? 0}{' '}
              {queryFacilities.data?.count === 1 ? 'facility' : 'facilities'}
            </span>
            <Pagination
              count={queryFacilities.data?.count ?? 0}
              pageSize={PAGE_SIZE}
              currentPage={currentPage}
              siblingCount={2}
              onChangePage={handleChangePage}
            />
          </div>
        </Col>
      </Row>

      {showAddFacilityButton && (
        <AddNewFacilityDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSubmit={handleAddFacility}
        />
      )}
    </>
  );
};

const TableRow = ({
  children,
  ...props
}: {
  children: React.ReactNode;
  'data-testid'?: string;
}) => <tr {...props}>{children}</tr>;

const SkeletonTableRows = () => {
  return Array(PAGE_SIZE)
    .fill(0)
    .map((_, index) => (
      <TableRow key={index} data-testid={`skeleton-row-${index}`}>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
      </TableRow>
    ));
};
